import { type FixedIncomeProduct } from 'investments/common/entities'

import { httpClientBanking } from '~/domains/banking/shared/infra/http-client-banking'
import { type PaginatedResponse } from '~/domains/platform/infra/http/paginated-client'
import { type UseInfiniteQueryRequestOptions, useInfiniteQueryRequest } from '~/ui/hooks/utils'

async function fetchFixedIncomeProductList({
  accountId,
  cursor,
  limit = 100
}: {
  accountId: string
  cursor?: string
  limit?: number
}) {
  const { data } = await httpClientBanking<PaginatedResponse<FixedIncomeProduct>>({
    url: `/investments/accounts/${accountId}/fixed_income_products`,
    params: { cursor, limit }
  })
  return data
}

export function getUseFetchFixedIncomeProductListKey(accountId: string, limit: number) {
  return ['useFetchFixedIncomeProductList', accountId, limit] as const
}

export function useFetchFixedIncomeProductList({
  accountId,
  limit = 100,
  options
}: {
  accountId: string
  limit?: number
  options?: UseInfiniteQueryRequestOptions<FixedIncomeProduct, true>
}) {
  const { data, ...rest } = useInfiniteQueryRequest<PaginatedResponse<FixedIncomeProduct>, unknown, true>(
    getUseFetchFixedIncomeProductListKey(accountId, limit),
    ({ pageParam }) => fetchFixedIncomeProductList({ accountId, cursor: pageParam, limit }),
    {
      getNextPageParam: lastPage => lastPage.cursor?.after,
      suspense: true,
      ...options
    }
  )

  return {
    data: data?.pages.flatMap(page => page.data) ?? [],
    ...rest
  }
}
